import './components.css';
import { initCanvas, type CanvasConfig, LIGHT, type Theme } from '@blog-components/canvas';
import { createPlugin, setupStepUI, findStepElements, parseSteps } from '@blog-components/sequence';
import { BordersPlugin } from '@blog-components/borders';
import { injectCanvasWorld, CANVAS_FILES, type CanvasId } from './canvases';

const CONCEPT_COLORS = {
  query: LIGHT.green,
  key: LIGHT.blue,
  value: LIGHT.yellow,
  attention: LIGHT.red,
} as const;

function toHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

function injectConceptColors(): void {
  const root = document.documentElement;
  for (const [name, color] of Object.entries(CONCEPT_COLORS)) {
    root.style.setProperty(`--color-${name}`, toHex(color));
  }
}

function renderKatexDescription(el: HTMLElement, description: string): void {
  el.innerHTML = description;
  el.querySelectorAll('.katex-inline').forEach((katexEl) => {
    const text = katexEl.textContent ?? '';
    if (text && window.katex) {
      katexEl.innerHTML = window.katex.renderToString(text, {
        displayMode: false,
        throwOnError: false,
      });
    }
  });
}

function createCanvasConfig(canvasId: CanvasId): CanvasConfig {
  const { steps, sequenceMap } = parseSteps(CANVAS_FILES[canvasId]);

  return {
    canvasId: `canvas-${canvasId}`,
    theme: LIGHT,
    createPlugin: () => [createPlugin(sequenceMap), BordersPlugin],
    beforeInit: (worldElement) => injectCanvasWorld(worldElement, canvasId),
    setupUI: (state: import('vibegame').State, _theme: Theme) => {
      const controller = state.getEntityByName(`controller-${canvasId}`);
      if (controller === null) return;

      const elements = findStepElements(`.step-navigator[data-canvas="${canvasId}"]`);
      if (!elements) return;

      setupStepUI(state, controller, {
        steps,
        elements,
        renderDescription: renderKatexDescription,
      });
    },
  };
}

const demos: CanvasConfig[] = (Object.keys(CANVAS_FILES) as CanvasId[]).map(createCanvasConfig);

function substituteColors(tex: string): string {
  return tex.replace(/\\color\{(\w+)\}/g, (_match, name) => {
    const color = CONCEPT_COLORS[name as keyof typeof CONCEPT_COLORS];
    return color !== undefined ? `\\color{${toHex(color)}}` : _match;
  });
}

function renderKatex(): void {
  if (typeof window === 'undefined' || !window.katex) return;

  const render = (el: Element, displayMode: boolean) => {
    const text = el.textContent ?? '';
    if (!text) return;
    const tex = substituteColors(text);
    el.innerHTML = window.katex!.renderToString(tex, { displayMode, throwOnError: false });
  };

  document.querySelectorAll('.katex-display').forEach((el) => render(el, true));
  document.querySelectorAll('.katex-inline').forEach((el) => render(el, false));
}

declare global {
  interface Window {
    katex?: {
      renderToString: (
        tex: string,
        options?: { displayMode?: boolean; throwOnError?: boolean }
      ) => string;
    };
  }
}

export function init(): void {
  injectConceptColors();
  initCanvas(demos);

  if (document.readyState === 'complete') {
    renderKatex();
  } else {
    window.addEventListener('load', renderKatex);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
